import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';

import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { identifyUser } from '@/lib/analytics';
import {
  type RelationshipFormData,
  relationshipFormId,
  relationshipFormSchema,
  relationshipOptions,
} from '@/types/user-profile.type';

export const Relationship = () => {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const {
    patientFirstName,
    representativeRelationship,
    representativeAuthorizedForMedicalDecisions,
    contactEmail,
  } = useSessionStore();

  const form = useForm<RelationshipFormData>({
    resolver: zodResolver(relationshipFormSchema),
    defaultValues: {
      relationship: representativeRelationship || undefined,
      authorizedForMedicalDecisions: representativeAuthorizedForMedicalDecisions || undefined,
    },
  });

  const authorizedForMedicalDecisions = form.watch('authorizedForMedicalDecisions');
  const showImportantNote = authorizedForMedicalDecisions === 'no';

  const onSubmit = (data: RelationshipFormData) => {
    const { updateState } = useSessionStore.getState();

    updateState({
      representativeRelationship: data.relationship,
      representativeAuthorizedForMedicalDecisions: data.authorizedForMedicalDecisions,
    });

    if (contactEmail) {
      identifyUser(contactEmail);
    }

    // Auto-save session (non-blocking)
    saveSession();

    navigate({ to: '/create-profile/for-someone-else/shipping' });
  };

  const handleBack = () => {
    navigate({ to: '/insurance/benefits' });
  };

  const patientName = patientFirstName || 'the patient';

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Results" />
      <Card>
        <CardContent>
          <form
            className="space-y-8 sm:space-y-12"
            onSubmit={form.handleSubmit(onSubmit)}
            id={relationshipFormId}
          >
            <FieldGroup>
              <div className="space-y-7">
                <Controller
                  name="relationship"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <FieldSet>
                      <FieldLegend className="mb-8 font-martina text-[32px] leading-[115%] font-normal tracking-tight">
                        What's your relationship to {patientName}?
                      </FieldLegend>
                      <RadioGroup
                        orientation="vertical"
                        name={field.name}
                        value={field.value ?? ''}
                        onValueChange={field.onChange}
                        aria-invalid={fieldState.invalid}
                      >
                        {relationshipOptions.map((option) => (
                          <Field
                            orientation="horizontal"
                            data-invalid={fieldState.invalid}
                            key={option.value}
                            className="flex w-auto items-center gap-2"
                          >
                            <RadioGroupItem
                              value={option.value}
                              id={`${relationshipFormId}-${option.value}`}
                              aria-invalid={fieldState.invalid}
                            />
                            <FieldLabel htmlFor={`${relationshipFormId}-${option.value}`}>
                              {option.label}
                            </FieldLabel>
                          </Field>
                        ))}
                      </RadioGroup>
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </FieldSet>
                  )}
                />

                <Controller
                  name="authorizedForMedicalDecisions"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <FieldSet>
                      <FieldLegend>
                        Are you authorized to make medical decisions on behalf of {patientName}?
                      </FieldLegend>
                      <RadioGroup
                        name={field.name}
                        value={field.value ?? ''}
                        onValueChange={field.onChange}
                        aria-invalid={fieldState.invalid}
                      >
                        {[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                        ].map((option) => (
                          <Field
                            orientation="horizontal"
                            data-invalid={fieldState.invalid}
                            key={option.value}
                            className="flex w-auto items-center gap-2"
                          >
                            <RadioGroupItem
                              value={option.value}
                              id={`${relationshipFormId}-authorized-${option.value}`}
                              aria-invalid={fieldState.invalid}
                            />
                            <FieldLabel
                              htmlFor={`${relationshipFormId}-authorized-${option.value}`}
                            >
                              {option.label}
                            </FieldLabel>
                          </Field>
                        ))}
                      </RadioGroup>
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </FieldSet>
                  )}
                />

                {showImportantNote && (
                  <CardDescription>
                    <p className="pb-1 font-bold text-orchid-600">Important note</p>
                    <p>
                      Since you're not an authorized proxy, the patient will need to be present for
                      the clinical phone call.
                    </p>
                  </CardDescription>
                )}
              </div>
            </FieldGroup>

            <Button type="submit" className="w-full">
              Continue
            </Button>
          </form>
        </CardContent>
      </Card>
      <DataPrivacyStatement />
    </section>
  );
};
